import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import UserLayout from "../../components/UserLayout";
import { api } from "../../utils/api";
import { useToast } from "../../contexts/ToastContext";


const STATUS_GROUPS = {
  all: [],

  under_review: [
    "Application Received, Pending for Review",
    "Review Passed - Pending for Submission",
    "Submission Done - Awaiting Visa Status"
  ],

  additional_info: [
    "Additional Information Requested",
    "Additional Information Received"
  ],

  submitted: [
    "Submission Done - Awaiting Visa Status"
  ],

  approved: ["Visa Approved"],
  rejected: ["Visa Rejected"]
};

export default function MyApplications() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [searchParams] = useSearchParams();

  const [applications, setApplications] = useState([]);
  const [filteredApps, setFilteredApps] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState(
    searchParams.get("status") || "all"
  );
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchApplications();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [applications, searchTerm, statusFilter]);

  const fetchApplications = async () => {
    try {
      const res = await api.getApplications();
      setApplications(res.data);
    } catch {
      showToast("Failed to load applications", "error");
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let result = [...applications];

    if (statusFilter !== "all") {
      result = result.filter(app =>
        STATUS_GROUPS[statusFilter]?.includes(app.status)
      );
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(app =>
        app.id.toLowerCase().includes(term) ||
        app.visa_type_name?.toLowerCase().includes(term) ||
        app.applicant_name?.toLowerCase().includes(term)
      );
    }

    setFilteredApps(result);
  };

  const formatDate = (date) =>
    date
      ? new Date(date).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
      : "-";

  const getStatusBadge = (status) => {
    const statusConfig = {
      "Application Received, Pending for Review": {
        color: "bg-amber-50 text-amber-700",
        icon: "⏳",
      },
      "Additional Information Requested": {
        color: "bg-orange-50 text-orange-700",
        icon: "📝",
      },
      "Additional Information Received": {
        color: "bg-indigo-50 text-indigo-700",
        icon: "✓",
      },
      "Review Passed - Pending for Submission": {
        color: "bg-blue-50 text-blue-700",
        icon: "📋",
      },
      "Submission Done - Awaiting Visa Status": {
        color: "bg-purple-50 text-purple-700",
        icon: "⏱️",
      },
      "Visa Approved": {
        color: "bg-emerald-50 text-emerald-700",
        icon: "✅",
      },
      "Visa Rejected": {
        color: "bg-red-50 text-red-700",
        icon: "❌",
      },
      draft: {
        color: "bg-slate-50 text-slate-600",
        icon: "📄",
      },
    };

    const config = statusConfig[status] || statusConfig.draft;

    return (
      <span
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md ${config.color}`}
      >
        <span>{config.icon}</span>
        {status}
      </span>
    );
  };

  const handleVisaDownload = async (applicationId) => {
    try {
      const response = await api.downloadVisa(applicationId);

      if (response.data && response.data.download_url) {
        const fileResponse = await fetch(response.data.download_url);
        const blob = await fileResponse.blob();

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          response.data.file_name || `visa_${applicationId}.pdf`,
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        showToast("Visa document downloaded successfully", "success");
      }
    } catch (error) {
      console.error("Visa download failed:", error);
      showToast("Failed to download visa document", "error");
    }
  };

  if (loading) {
    return (
      <UserLayout>
        <div className="flex items-center justify-center min-h-[50vh]">
          <div className="text-center">
            <div className="animate-spin h-8 w-8 border-3 border-blue-600 border-t-transparent rounded-full mx-auto mb-3"></div>
            <p className="text-sm text-slate-600 font-medium">
              Loading applications...
            </p>
          </div>
        </div>
      </UserLayout>
    );
  }

  return (
    <UserLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">

        {/* HEADER */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-xl font-bold text-slate-900">My Applications</h1>
              <p className="text-sm text-slate-600 mt-0.5">
                View and manage all your visa applications
              </p>
            </div>
            <button
              onClick={() => navigate("/create-new-application")}
              className="inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg shadow-sm hover:shadow transition-all duration-200"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              New Application
            </button>
          </div>
        </div>

        {/* FILTERS */}
        <div className="bg-white border border-slate-200 rounded-lg p-4 mb-6 shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

            {/* Search Input */}
            <div className="relative">
              <svg
                className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <input
                type="text"
                placeholder="Search by ID, Name, or Visa Type..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-4 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
            >
              <option value="all">All Status</option>
              <option value="under_review">Under Review</option>
              <option value="additional_info">Additional Information</option>
              <option value="submitted">Submitted</option>
              <option value="approved">Visa Approved</option>
              <option value="rejected">Visa Rejected</option>
            </select>

          </div>
        </div>

        {/* APPLICATIONS TABLE */}
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-200 bg-slate-50/50">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-slate-900">
                Applications ({filteredApps.length})
              </h3>
            </div>
          </div>

          {filteredApps.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">
                      Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">
                      Application ID
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">
                      Applicant
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">
                      Visa Type
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">
                      Status
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wide">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 bg-white">
                  {filteredApps.map((app) => (
                    <tr
                      key={app.id}
                      className="hover:bg-slate-50 transition-colors duration-150"
                    >
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600">
                        {formatDate(app.created_at)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-sm font-mono text-slate-700">
                          {app.id}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-sm font-medium text-slate-900">
                          {app.applicant_name || "N/A"}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm font-medium text-slate-900">
                          {app.visa_category}
                        </div>
                        <div className="text-xs text-slate-500">
                          {app.visa_type_name}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {getStatusBadge(app.status)}
                      </td>

                      <td className="px-4 py-3 whitespace-nowrap text-right">
                        <div className="inline-flex items-center gap-1.5">

                          {/* VIEW ICON - Always visible for ALL applications */}
                          <button
                            onClick={() => navigate(`/applications/${app.id}`)}
                            className="inline-flex items-center justify-center w-8 h-8 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors"
                            title="View Application"
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                              />
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                              />
                            </svg>
                          </button>

                          {/* EDIT ICON - Only visible for DRAFT status */}
                          {app.status === "draft" && (
                            <button
                              onClick={() => navigate(`/applications/${app.id}?mode=edit`)}
                              className="inline-flex items-center justify-center w-8 h-8 text-green-600 hover:text-green-700 hover:bg-green-50 rounded-md transition-colors"
                              title="Edit Application"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                />
                              </svg>
                            </button>
                          )}

                          {/* DOWNLOAD ICON - Only for approved applications */}
                          {app.visa_uploaded === true && (
                            <button
                              onClick={() => handleVisaDownload(app.id)}
                              className="inline-flex items-center justify-center w-8 h-8 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 rounded-md transition-colors"
                              title="Download Visa"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                                />
                              </svg>
                            </button>
                          )}

                        </div>
                      </td>

                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-12 text-center">
              <div className="w-14 h-14 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3 text-2xl">
                {searchTerm || statusFilter !== "all" ? "🔍" : "📋"}
              </div>
              <h4 className="text-sm text-slate-900 font-semibold mb-1">
                {searchTerm || statusFilter !== "all"
                  ? "No applications found"
                  : "No applications yet"}
              </h4>
              <p className="text-xs text-slate-500 mb-4">
                {searchTerm || statusFilter !== "all"
                  ? "Try adjusting your search or filters"
                  : "Get started by creating your first application"}
              </p>
              {!searchTerm && statusFilter === "all" && (
                <button
                  onClick={() => navigate("/create-new-application")}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-xs bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg shadow-sm transition-all duration-200"
                >
                  <svg
                    className="w-3.5 h-3.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                  Create Application
                </button>
              )}
            </div>
          )}
        </div>

      </div>
    </UserLayout>
  );
}